import {useCallback} from 'react';
import {useWineshopWarningText} from 'plugins/modules/filter/components/ExpressOrder/hooks/useWineshopWarningText';
import {IExpressOrder} from 'plugins/modules/filter/types';
import {EventAction, EventCategory, EventNames} from 'modules/google-analytics/constants';
import {useGtagEventLocation} from 'hooks/analytics/gtag/helpers/useGtagEventLocation';
import {sendGtagEvent} from 'hooks/analytics/gtag/helpers';
const VISIT_KEY = 'EXPRESS_ORDER_VISIT';

type TShowWineryPopupSource = 'plp' | 'pdp' | 'header' | 'unset';

export const useExpressOrderGtag = (wineShop?: IExpressOrder) => {
    const warningText = useWineshopWarningText(wineShop);
    const {eventLocation} = useGtagEventLocation();

    const sendSwitchOnEvent = useCallback(() => {
        const dataLayerList = {
            eventCategory: 'Conversions_checkout_today',
            event: EventNames.OWOX,
            eventAction: 'switchOn',
            eventLabel: 'Включить забрать сегодня',
            ...(warningText && {eventContent: warningText}),
        };
        sendGtagEvent(dataLayerList);
    }, [warningText]);

    const sendSwitchOffEvent = useCallback(() => {
        const dataLayerList = {
            eventCategory: 'Conversions_checkout_today',
            event: EventNames.OWOX,
            eventAction: 'switchOff',
            eventLabel: 'Отключить забрать сегодня',
            ...(warningText && {eventContent: warningText}),
        };
        sendGtagEvent(dataLayerList);
    }, [warningText]);

    const sendVisitEvent = useCallback(
        (eventLabel: string) => {
            const isTracked = Boolean(window?.sessionStorage?.getItem(VISIT_KEY));

            if (isTracked) {
                return;
            }

            const dataLayerList = {
                event: EventNames.OWOX,
                eventCategory: EventCategory.AbHeaderWinery,
                eventAction: EventAction.Start,
                eventLabel,
                eventLocation,
            };

            sendGtagEvent(dataLayerList);

            window?.sessionStorage?.setItem(VISIT_KEY, 'true');
        },
        [eventLocation],
    );

    const sendDiscardEvent = useCallback(
        (wineryTitle: string) => {
            const dataLayerList = {
                event: EventNames.OWOX,
                eventCategory: EventCategory.AbHeaderWinery,
                eventAction: 'drop_winery',
                eventLabel: `${wineryTitle} to notSet`,
                eventLocation,
            };

            sendGtagEvent(dataLayerList);
        },
        [eventLocation],
    );

    const sendChangeEvent = useCallback(
        (nextWineryTitle: string, prevWineryTitle?: string) => {
            const dataLayerList = {
                event: EventNames.OWOX,
                eventCategory: EventCategory.AbHeaderWinery,
                eventAction: prevWineryTitle ? 'change_winery' : 'set_winery',
                eventLabel: `${prevWineryTitle ?? 'notSet'} to ${nextWineryTitle}`,
                eventLocation,
            };

            sendGtagEvent(dataLayerList);
        },
        [eventLocation],
    );

    const sendClickPopupEvent = useCallback(
        (hasWinery: boolean) => {
            const dataLayerList = {
                event: EventNames.OWOX,
                eventCategory: EventCategory.AbHeaderWinery,
                eventAction: EventAction.Click,
                eventLabel: hasWinery ? 'change_popup' : 'first_popup',
                eventLocation,
            };

            sendGtagEvent(dataLayerList);
        },
        [eventLocation],
    );

    const sendShowPopupEvent = useCallback(
        (hasWinery: boolean) => {
            const dataLayerList = {
                event: EventNames.OWOX,
                eventCategory: EventCategory.AbHeaderWinery,
                eventAction: EventAction.Show,
                eventLabel: hasWinery ? 'change_popup' : 'first_popup',
                eventLocation,
            };
            sendGtagEvent(dataLayerList);
        },
        [eventLocation],
    );

    const sendClosePopupEvent = useCallback(
        (hasWinery: boolean) => {
            const dataLayerList = {
                event: EventNames.OWOX,
                eventCategory: EventCategory.AbHeaderWinery,
                eventAction: EventAction.Close,
                eventLabel: hasWinery ? 'change_popup' : 'first_popup',
                eventLocation,
            };

            sendGtagEvent(dataLayerList);
        },
        [eventLocation],
    );

    const sendCloseWineryPopupEvent = useCallback(() => {
        const dataLayerList = {
            event: EventNames.OWOX,
            eventCategory: EventCategory.AbHeaderWinery,
            eventAction: EventAction.Close,
            eventLabel: 'chose_popup',
            eventLocation,
        };

        sendGtagEvent(dataLayerList);
    }, [eventLocation]);

    const sendShowWineryPopupEvent = useCallback(
        (source: TShowWineryPopupSource = 'unset') => {
            const dataLayerList = {
                event: EventNames.OWOX,
                eventCategory: EventCategory.AbHeaderWinery,
                eventAction: EventAction.Show,
                eventLabel: `chose_popup_${source}`,
                eventLocation,
            };

            sendGtagEvent(dataLayerList);
        },
        [eventLocation],
    );

    const sendClickWineryPopupEvent = useCallback(() => {
        const dataLayerList = {
            event: EventNames.OWOX,
            eventCategory: EventCategory.AbHeaderWinery,
            eventAction: EventAction.Click,
            eventLabel: 'chose_popup',
            eventLocation,
        };

        sendGtagEvent(dataLayerList);
    }, [eventLocation]);

    return {
        sendSwitchOnEvent,
        sendSwitchOffEvent,
        sendDiscardEvent,
        sendVisitEvent,
        sendChangeEvent,
        sendClosePopupEvent,
        sendClickPopupEvent,
        sendShowPopupEvent,
        sendCloseWineryPopupEvent,
        sendShowWineryPopupEvent,
        sendClickWineryPopupEvent,
    };
};
